import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Popover,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link } from 'react-router-dom';
import StyledButton from '../Button/StyledButton';
import useAuth from '../../../hooks/useAuth';
import { useSubscription } from '../../../contexes/SubscriptionProvider';
import { usageAmount } from '../../../helpers/usageAmount';
import { resolveUsageLimitsFromPlan } from '../../../helpers/planUsageLimits';
import { resolveSubscribedPlanNavLabel } from '../../../helpers/navbarPlanLabel';
import { shouldShowUpgradeCta } from '../../../helpers/billingPlanSwitchUi';
import { USAGE_DATA_REFRESH_EVENT } from '../../../helpers/usageEvents';
import {
  creditsCapForDisplay,
  creditsUsedForDisplay,
  enrichUsageWithCompany,
  enterpriseCreditsConsumptionPercent,
  resolvedPoolRemaining,
  showCreditPoolSection,
} from '../../../helpers/creditUsageDisplay';
import { useTranslation } from '../../../hooks/useTranslation';
import { fetchUsage, UsageResponse } from '../../../services/billingService';
import NavBarPlanBadge from './NavBarPlanBadge';
import OverageUsageMeter from '../../billing/OverageUsageMeter';
import { canPerformAction, PERMISSIONS } from '../../../utils/permissions';

function overallUsagePercent(
  minutesUsed: number,
  minutesLimit: number,
  messagesUsed: number,
  messagesLimit: number
): number {
  const hasMin = minutesLimit > 0;
  const hasMsg = messagesLimit > 0;
  if (!hasMin && !hasMsg) return 0;
  if (hasMin && hasMsg) {
    return (
      (Math.min(100, (minutesUsed / minutesLimit) * 100) +
        Math.min(100, (messagesUsed / messagesLimit) * 100)) /
      2
    );
  }
  if (hasMin) return Math.min(100, (minutesUsed / minutesLimit) * 100);
  return Math.min(100, (messagesUsed / messagesLimit) * 100);
}

/**
 * Plan label + circular usage % with a popover for details (hover on desktop, tap on touch).
 */
const UsageQuotaTopBar: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const prefersHover = useMediaQuery('(hover: hover)');
  const { currentUser } = useAuth();
  const canManageBilling = Boolean(
    currentUser && canPerformAction(currentUser, PERMISSIONS.MANAGE_BILLING)
  );
  const { company, subscription } = useSubscription();
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const refreshUsageSafe = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!canManageBilling || !currentUser?.company_id) {
        setLoading(false);
        return;
      }
      if (!opts?.silent) {
        setLoading(true);
      }
      const data = await fetchUsage(currentUser.company_id as string);
      setUsage(data ?? null);
      setLoading(false);
    },
    [canManageBilling, currentUser?.company_id]
  );

  useEffect(() => {
    if (!canManageBilling) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const run = async (opts?: { silent?: boolean }) => {
      if (cancelled) return;
      await refreshUsageSafe(opts);
    };
    void run();
    const onUsageRefresh = () => {
      void run({ silent: true });
    };
    const onWindowFocus = () => {
      void run({ silent: true });
    };
    window.addEventListener(USAGE_DATA_REFRESH_EVENT, onUsageRefresh);
    window.addEventListener('focus', onWindowFocus);
    return () => {
      cancelled = true;
      window.removeEventListener(USAGE_DATA_REFRESH_EVENT, onUsageRefresh);
      window.removeEventListener('focus', onWindowFocus);
    };
  }, [canManageBilling, refreshUsageSafe]);

  const displayUsage = useMemo(
    () => enrichUsageWithCompany(usage, company),
    [usage, company]
  );

  const minutesUsed = displayUsage ? usageAmount(displayUsage.minutes_used) : 0;
  const messagesUsed = displayUsage
    ? usageAmount(displayUsage.messages_used)
    : 0;
  const { minutesLimit, messagesLimit } = resolveUsageLimitsFromPlan(
    displayUsage?.plan_id ?? company?.plan_id,
    displayUsage?.minutes_limit ?? null,
    displayUsage?.messages_limit ?? null
  );

  const creditPoolMode = Boolean(
    displayUsage && showCreditPoolSection(displayUsage)
  );
  const creditsCap = displayUsage ? creditsCapForDisplay(displayUsage) : null;

  const hasMinuteCap = minutesLimit !== null;
  const hasMessageCap = messagesLimit !== null;
  // Free tier can use both call and chat against the shared demo pool — always
  // show both meters. Paid single-line plans hide the unsubscribed product line.
  const isFreeTier =
    (displayUsage?.plan_tier ?? company?.plan_tier ?? 'free') === 'free';
  const showCallMinutesInPopover =
    isFreeTier ||
    !creditPoolMode ||
    creditsCap == null ||
    displayUsage?.plan_type !== 'chat';
  const showChatMessagesInPopover =
    isFreeTier ||
    !creditPoolMode ||
    creditsCap == null ||
    displayUsage?.plan_type !== 'call';

  const percent = useMemo(() => {
    if (!displayUsage) return 0;
    if (displayUsage.bundle_credit_pool) {
      return enterpriseCreditsConsumptionPercent(displayUsage);
    }
    const overallPct = overallUsagePercent(
      minutesUsed,
      minutesLimit ?? 0,
      messagesUsed,
      messagesLimit ?? 0
    );
    if (creditPoolMode && creditsCap != null) {
      return enterpriseCreditsConsumptionPercent(displayUsage);
    }
    if (creditPoolMode) {
      return Math.max(
        enterpriseCreditsConsumptionPercent(displayUsage),
        overallPct
      );
    }
    return overallPct;
  }, [
    creditPoolMode,
    creditsCap,
    displayUsage,
    minutesUsed,
    minutesLimit,
    messagesUsed,
    messagesLimit,
  ]);
  const planLabel = useMemo(
    () =>
      resolveSubscribedPlanNavLabel(company, subscription) || t('Free plan'),
    [company, subscription, t]
  );
  const showUpgradeCta = shouldShowUpgradeCta(company, subscription);

  const creditCap = displayUsage ? creditsCapForDisplay(displayUsage) : null;
  const hasCreditsCap = creditCap != null && creditCap > 0;
  const creditsUsed = displayUsage ? creditsUsedForDisplay(displayUsage) : 0;
  const creditsRemainingValue =
    displayUsage != null
      ? Math.round((resolvedPoolRemaining(displayUsage) ?? 0) * 10) / 10
      : 0;

  const minutesLine = showCallMinutesInPopover
    ? hasMinuteCap
      ? `${minutesUsed}/${minutesLimit ?? 0}`
      : `${minutesUsed}/—`
    : null;
  const messagesLine = showChatMessagesInPopover
    ? hasMessageCap
      ? `${messagesUsed}/${messagesLimit ?? 0}`
      : `${messagesUsed}/—`
    : null;

  const minutesBar =
    hasMinuteCap && (minutesLimit ?? 0) > 0
      ? Math.min(100, (minutesUsed / (minutesLimit ?? 1)) * 100)
      : 0;
  const messagesBar =
    hasMessageCap && (messagesLimit ?? 0) > 0
      ? Math.min(100, (messagesUsed / (messagesLimit ?? 1)) * 100)
      : 0;

  const handleClose = () => setAnchorEl(null);

  const openPopover = (el: HTMLElement) => {
    setAnchorEl(el);
    void refreshUsageSafe({ silent: true });
  };

  const handleMouseEnter = (el: HTMLElement) => {
    if (prefersHover) {
      openPopover(el);
    }
  };

  const handleMouseLeave = () => {
    if (prefersHover) {
      handleClose();
    }
  };

  const boxRef = React.useRef<HTMLDivElement>(null);

  if (!currentUser || !canManageBilling) return null;

  return (
    <Box
      ref={boxRef}
      onMouseEnter={e => handleMouseEnter(e.currentTarget)}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        cursor: 'pointer',
      }}
      onClick={e => {
        if (!prefersHover) {
          e.stopPropagation();
          if (anchorEl) {
            handleClose();
          } else {
            openPopover(e.currentTarget as HTMLElement);
          }
        }
      }}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (anchorEl) {
            handleClose();
          } else if (boxRef.current) {
            openPopover(boxRef.current);
          }
        }
      }}
      aria-haspopup="true"
      aria-expanded={open ? 'true' : 'false'}
      aria-label={t('Usage')}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <NavBarPlanBadge label={planLabel} compact embeddedInTopBar />
        <Box
          sx={{
            position: 'relative',
            width: 44,
            height: 44,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Full grey track; primary arc only when usage > 0 */}
          <CircularProgress
            variant="determinate"
            value={100}
            size={44}
            thickness={4}
            sx={theme => ({
              color:
                theme.palette.mode === 'dark'
                  ? theme.palette.grey[700]
                  : theme.palette.grey[300],
            })}
          />
          {!loading && percent > 0 && (
            <CircularProgress
              variant="determinate"
              value={percent}
              size={44}
              thickness={4}
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                color: 'primary.main',
              }}
            />
          )}
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              fontSize: '0.7rem',
              fontWeight: 700,
              color: 'text.primary',
            }}
          >
            {loading ? '—' : `${Math.round(percent)}%`}
          </Typography>
        </Box>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableRestoreFocus
        disablePortal
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: theme.zIndex.modal }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 280,
              maxWidth: 320,
              p: 2,
              borderRadius: 3,
              bgcolor: 'background.paper',
              border: '1px solid rgba(15, 23, 42, 0.06)',
              boxShadow: '0px 10px 28px rgba(15, 23, 42, 0.08)',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1.5,
          }}
        >
          <Typography
            variant="subtitle2"
            fontWeight={600}
            color="text.primary"
            sx={{ fontFamily: 'inherit' }}
          >
            {t('Usage')}
          </Typography>
          {showUpgradeCta ? (
            <Link
              to="/pricing"
              style={{ textDecoration: 'none' }}
              onClick={handleClose}
            >
              <StyledButton
                size="small"
                variant="contained"
                color="primary"
                sx={{ minWidth: 'auto', px: 1.5 }}
              >
                {t('Upgrade')}
              </StyledButton>
            </Link>
          ) : null}
        </Box>

        {creditPoolMode && displayUsage ? (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 0.5 }}
            >
              {t('Balance')}
            </Typography>
            {hasCreditsCap ? (
              <LinearProgress
                color="primary"
                variant="determinate"
                value={
                  loading
                    ? 0
                    : enterpriseCreditsConsumptionPercent(displayUsage)
                }
                sx={{ height: 6, borderRadius: 1, mb: 0.5 }}
              />
            ) : null}
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {loading
                ? t('Loading...')
                : hasCreditsCap
                  ? t('Credits balance label', {
                      used: creditsUsed,
                      remaining: creditsRemainingValue,
                    })
                  : t('Credits balance unlimited label', {
                      used: creditsUsed,
                    })}
            </Typography>
          </>
        ) : null}
        {showCallMinutesInPopover ? (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 0.5 }}
            >
              {t('Call minutes')}
            </Typography>
            <LinearProgress
              color="primary"
              variant="determinate"
              value={loading ? 0 : minutesBar}
              sx={{ height: 6, borderRadius: 1, mb: 0.5 }}
            />
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {loading ? t('Loading...') : (minutesLine ?? '—')}
            </Typography>
          </>
        ) : null}

        {showChatMessagesInPopover ? (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 0.5 }}
            >
              {t('Chat messages')}
            </Typography>
            <LinearProgress
              color="primary"
              variant="determinate"
              value={loading ? 0 : messagesBar}
              sx={{ height: 6, borderRadius: 1, mb: 0.5 }}
            />
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {loading ? t('Loading...') : (messagesLine ?? '—')}
            </Typography>
          </>
        ) : null}
        {displayUsage ? (
          <OverageUsageMeter usage={displayUsage} compact loading={loading} />
        ) : null}

        <Link
          to="/usage"
          style={{ textDecoration: 'none', display: 'block' }}
          onClick={handleClose}
        >
          <StyledButton
            size="small"
            fullWidth
            variant="outlined"
            color="primary"
          >
            {t('View usage')}
          </StyledButton>
        </Link>
      </Popover>
    </Box>
  );
};

export default UsageQuotaTopBar;
